import React, { useEffect, useState } from "react";
import { Table, Tabs, Input, Modal, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import MessagesStyle, { DeleteModalContent } from "./style";
import FetchData from "../../../hooks/FetchData";
import PostUpdateData from "../../../hooks/postUpdate";
import useMailbox from "./useMailbox";
// import { ReactComponent as ReplyIcon } from "../../../assets/Images/Reply.svg";
// import { ReactComponent as ForwardIcon } from "../../../assets/Images/Forward.svg";
import { ReactComponent as ReplyIcon } from "../../../assets/reverse.svg";
import { ReactComponent as ForwardIcon } from "../../../assets/forward.svg";
import { ReactComponent as TrashFull } from "../../../assets/Images/Trash_Full.svg";
import { ReactComponent as PaperclipIcon } from "../../../assets/Paperclip_Attechment_Tilt.svg";

const ordinalSuffix = (day) =>
  day % 10 === 1 && day !== 11
    ? "st"
    : day % 10 === 2 && day !== 12
    ? "nd"
    : day % 10 === 3 && day !== 13
    ? "rd"
    : "th";

// "25th Aug 2024" - matches the date format used across the Dashboard's
// own To-do rows and the Course table's "Last Updated on" column.
const formatShortOrdinalDate = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const day = date.date();
  return `${day}${ordinalSuffix(day)} ${date.format("MMM YYYY")}`;
};

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = useSelector((state) => state.authTracking.userInfo?.clientId);
  const { needsPerson, peopleId, query } = useMailbox();

  // ViewMessage's "Sent" breadcrumb/Back link comes here via
  // /messages?tab=sent so returning from a sent message lands back on
  // the Sent tab instead of always resetting to Inbox.
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "sent" ? "sent" : "inbox");
  const [inboxData, setInboxData] = useState([]);
  const [sentData, setSentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadInbox = () => {
    if (!clientId) return;
    setLoading(true);
    FetchData({ endPoint: `/message/getinbox/${clientId}${query}` })
      .then((res) => setInboxData(res?.response || []))
      .catch(() => message.error("Failed to load inbox"))
      .finally(() => setLoading(false));
  };

  const loadSent = () => {
    if (!clientId) return;
    setLoading(true);
    FetchData({ endPoint: `/message/getsent/${clientId}${query}` })
      .then((res) => setSentData(res?.response || []))
      .catch(() => message.error("Failed to load sent messages"))
      .finally(() => setLoading(false));
  };

  // Also reloads when the header's View As person changes - each person
  // has their own Inbox/Sent.
  useEffect(() => {
    if (!clientId || needsPerson) return;
    if (activeTab === "inbox") loadInbox();
    else loadSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, activeTab, peopleId, needsPerson]);

  const confirmDelete = () => {
    const { messageId, scope } = deleteTarget;
    setDeleteTarget(null);
    const endPoint =
      scope === "sent"
        ? `/message/deletesent/${clientId}/${messageId}${query}`
        : `/message/deletemessage/${clientId}/${messageId}${query}`;
    PostUpdateData({ endPoint, method: "DELETE" })
      .then(() => {
        message.success("Message deleted");
        if (scope === "sent") {
          setSentData((prev) => prev.filter((item) => item.message_id !== messageId));
        } else {
          setInboxData((prev) => prev.filter((item) => item.message_id !== messageId));
        }
      })
      .catch(() => message.error("Failed to delete message"));
  };

  const inboxColumns = [
    {
      title: "Name",
      dataIndex: "sender_name",
      key: "sender_name",
      render: (text) => <span className="message-sender-name">{text}</span>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Received on",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (value) => formatShortOrdinalDate(value),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (text, record) => (
        <div className="table_action_icons">
          <ReplyIcon
            className="replyIcon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/messages/reply/${record.message_id}`);
            }}
          />
          <ForwardIcon
            className="forwardIcon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/messages/forward/${record.message_id}`);
            }}
          />
          <TrashFull
            className="deleteIcon"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget({ messageId: record.message_id, scope: "inbox" });
            }}
          />
        </div>
      ),
    },
  ];

  const sentColumns = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Sent to",
      dataIndex: "recipient_names",
      key: "recipient_names",
      render: (names) => (names || []).filter(Boolean).join(", "),
    },
    {
      title: "Sent on",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (value, record) => (
        <span className="message-date-cell">
          {formatShortOrdinalDate(value)}
          {record.has_attachment && <PaperclipIcon className="message-attachment-flag" width={20} height={20} />}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (text, record) => (
        <div className="table_action_icons">
          <ForwardIcon
            className="forwardIcon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/messages/forward/${record.message_id}`);
            }}
          />
          <TrashFull
            className="deleteIcon"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget({ messageId: record.message_id, scope: "sent" });
            }}
          />
        </div>
      ),
    },
  ];

  const search = searchText.trim().toLowerCase();
  const filteredInbox = inboxData.filter((item) => {
    if (!search) return true;
    return (
      item.sender_name?.toLowerCase().includes(search) ||
      item.subject?.toLowerCase().includes(search)
    );
  });
  const filteredSent = sentData.filter((item) => {
    if (!search) return true;
    return (
      item.subject?.toLowerCase().includes(search) ||
      (item.recipient_names || []).some((name) => name?.toLowerCase().includes(search))
    );
  });

  if (needsPerson) {
    return (
      <MessagesStyle>
        <div className="message-header">
          <p className="message-title">Message</p>
        </div>
        <p className="message-empty-hint">
          Pick who you're viewing as from the "Learner" dropdown in the header to see their
          messages.
        </p>
      </MessagesStyle>
    );
  }

  return (
    <MessagesStyle>
      <div className="message-header">
        <p className="message-title">Message</p>
        <button className="send-message-button" onClick={() => navigate("/messages/compose")}>
          Send New Message
        </button>
      </div>

      <Tabs
        className="message-tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "inbox", label: "Inbox" },
          { key: "sent", label: "Sent" },
        ]}
      />

      <Input
        className="message-search"
        prefix={<SearchOutlined />}
        placeholder="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        allowClear
      />

      {activeTab === "inbox" ? (
        <Table
          dataSource={filteredInbox}
          columns={inboxColumns}
          loading={loading}
          rowKey="message_id"
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          onRow={(record) => ({
            onClick: () => navigate(`/messages/view/${record.message_id}`),
          })}
        />
      ) : (
        <Table
          dataSource={filteredSent}
          columns={sentColumns}
          loading={loading}
          rowKey="message_id"
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          onRow={(record) => ({
            onClick: () => navigate(`/messages/view/${record.message_id}`),
          })}
        />
      )}

      <Modal
        open={!!deleteTarget?.messageId}
        footer={false}
        closable={false}
        centered
        width={480}
        onCancel={() => setDeleteTarget(null)}
      >
        <DeleteModalContent>
          <p className="delete-modal-title">Delete the message</p>
          <p className="delete-modal-body">Are you sure you want to delete this message?</p>
          <div className="delete-modal-footer">
            <button type="button" className="delete-modal-cancel" onClick={() => setDeleteTarget(null)}>
              Cancel
            </button>
            <button type="button" className="delete-modal-confirm" onClick={confirmDelete}>
              Delete
            </button>
          </div>
        </DeleteModalContent>
      </Modal>
    </MessagesStyle>
  );
};

export default Messages;
